import { slotCache, apiResponseCache } from '../utils/cacheManager';
import { CachedSlotData } from '../types/booking';

// WebSocket connection
let socket: WebSocket | null = null;
const SOCKET_RECONNECT_DELAY = 3000; // 3 seconds delay for reconnection attempts

// Connection state
let isConnecting = false;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 5;

/**
 * Strip protocol prefix from a URL to get just the host (and optional port/path).
 * Handles: "https://example.com", "wss://example.com", "example.com" → "example.com"
 */
const stripProtocol = (url: string): string => {
  return url.replace(/^(https?:\/\/|wss?:\/\/)/, '');
};

/**
 * Initialize the cache invalidation service with WebSocket connection
 */
export const initCacheInvalidationService = (): void => {
  // Require explicit WS host configuration to avoid connecting to static hosting origins
  const configuredHost = process.env.REACT_APP_WS_URL;
  if (!configuredHost) {
    return;
  }

  // Don't initialize if we're already connecting or have a socket
  if (isConnecting || socket) {
    return;
  }

  isConnecting = true;

  try {
    // Build WebSocket URL from configured host, stripping any protocol prefix
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = stripProtocol(configuredHost);
    const wsUrl = `${wsProtocol}//${wsHost}/ws/cache-events`;

    // Create WebSocket connection
    socket = new WebSocket(wsUrl);
    
    // Connection opened
    socket.addEventListener('open', () => {
      isConnecting = false;
      reconnectAttempts = 0;
    });

    // Listen for messages
    socket.addEventListener('message', (event) => {
      try {
        const data = JSON.parse(event.data);
        handleCacheInvalidationEvent(data);
      } catch (_) {
        // Silently ignore unparseable messages
      }
    });

    // Connection closed
    socket.addEventListener('close', () => {
      socket = null;
      isConnecting = false;

      if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        // Attempt to reconnect with exponential backoff
        reconnectAttempts++;
        const delay = SOCKET_RECONNECT_DELAY * Math.pow(2, reconnectAttempts - 1);

        setTimeout(() => {
          initCacheInvalidationService();
        }, delay);
      }
      // Silently give up after max attempts — WebSocket is optional for UX
    });

    // Connection error — close to trigger reconnection via close handler
    socket.addEventListener('error', () => {
      socket?.close();
    });
  } catch (_) {
    isConnecting = false;
  }
};

/**
 * Handle cache invalidation events from WebSocket
 */
const handleCacheInvalidationEvent = (event: any): void => {
  if (!event || !event.type) {
    return;
  }
  
  switch (event.type) {
    case 'booking_created':
    case 'booking_updated':
    case 'booking_cancelled':
      // Invalidate API response cache for the affected meeting type and date
      if (event.meetingTypeId && event.dateKey) {
        invalidateMeetingTypeSlots(event.meetingTypeId, event.dateKey);
      }

      // Dispatch booking status change event for Dashboard and other components
      dispatchBookingStatusChange(event);

      // Dispatch post-commit update for BookingConfirmation page
      // (when booking_updated contains video integration fields from server post-commit)
      if (event.bookingId && event.videoIntegrationStatus) {
        dispatchBookingPostCommitUpdate(event);
      }
      break;
    
    case 'meeting_type_updated':
      // Invalidate all cache for a meeting type
      if (event.meetingTypeId) {
        invalidateAllMeetingTypeCache(event.meetingTypeId);
      }
      break;
      
    case 'user_availability_updated':
      // Invalidate all cached slots for a user's meeting types
      if (event.userId && event.meetingTypeIds) {
        event.meetingTypeIds.forEach((meetingTypeId: string | number) => {
          invalidateAllMeetingTypeCache(meetingTypeId);
        });

        // Dispatch custom event for components to refresh meeting type data
        dispatchAvailabilityChange(event);
      }
      break;
      
    case 'global_cache_clear':
      // Clear all caches
      clearAllCaches();
      break;

    case 'organization_activity':
      // Dispatch organization activity event for admin panel
      if (event.organizationId && event.activityLog) {
        dispatchOrganizationActivityChange(event);
      }
      break;

    case 'connection_established':
      // Server acknowledgment — no action needed
      break;

    default:
      // Silently ignore unknown event types for forward compatibility
      break;
  }
};

/**
 * Invalidate cache for a specific meeting type and date
 */
export const invalidateMeetingTypeSlots = (
  meetingTypeId: string | number,
  dateKey: string
): void => {
  // Clear the API response cache for this specific date
  apiResponseCache.deleteByPrefix(`slots_${meetingTypeId}_${dateKey}`);
  
  // We also need to update the month cache to remove this date's data
  // This is more complex as we need to update the month object but not delete it entirely
  
  // Parse the date to extract year and month for the month key
  try {
    const [year, month] = dateKey.split('-');
    if (year && month) {
      const monthKeyPrefix = `${meetingTypeId}-${year}-${month}`;
      
      // Find all month cache keys that might contain this date
      const monthKeys: string[] = [];
      
      // This is a workaround since we can't directly get all keys with a prefix
      // Ideally we would add a method to the CacheManager to get all keys matching a pattern
      for (let tz of ['', '-UTC', '-America/New_York', '-Europe/London', '-Asia/Tokyo']) {
        const monthKey = `${monthKeyPrefix}${tz}`;
        if (slotCache.has(monthKey, true)) {
          monthKeys.push(monthKey);
        }
      }
      
      // Update each affected month cache
      monthKeys.forEach(monthKey => {
        const monthData = slotCache.get<CachedSlotData>(monthKey, true);
        if (monthData && monthData.slots[dateKey]) {
          // Create a new month data object without the invalidated date
          const newSlots = { ...monthData.slots };
          delete newSlots[dateKey];
          
          const updatedMonthData = {
            ...monthData,
            slots: newSlots,
            // Mark as incomplete so it will be refreshed on next access
            isComplete: false,
            // Update timestamp to reflect the change
            fetchedAt: Date.now()
          };

          slotCache.set(monthKey, updatedMonthData);
        }
      });
    }
  } catch (_) {
    // Silently handle parse errors — stale cache will be refreshed on next access
  }
};

/**
 * Invalidate all cache for a meeting type
 */
export const invalidateAllMeetingTypeCache = (
  meetingTypeId: string | number
): void => {
  // Clear API response cache
  apiResponseCache.deleteByPrefix(`slots_${meetingTypeId}`);
  
  // Clear month cache
  slotCache.deleteByPrefix(`${meetingTypeId}`);
};

/**
 * Clear all caches
 */
export const clearAllCaches = (): void => {
  apiResponseCache.clear();
  slotCache.clear();
};

/**
 * Dispatch booking status change events to other components
 */
const dispatchBookingStatusChange = (event: any): void => {
  // Create custom event for Dashboard and other components to listen to
  const customEvent = new CustomEvent('booking-status-changed', {
    detail: {
      type: event.type,
      meetingTypeId: event.meetingTypeId,
      dateKey: event.dateKey,
      timestamp: event.timestamp
    }
  });

  window.dispatchEvent(customEvent);
};

/**
 * Dispatch availability change events to other components
 */
const dispatchAvailabilityChange = (event: any): void => {
  // Create custom event for MeetingTypes and other components to listen to
  const customEvent = new CustomEvent('availability-changed', {
    detail: {
      type: event.type,
      userId: event.userId,
      meetingTypeIds: event.meetingTypeIds,
      timestamp: event.timestamp
    }
  });

  window.dispatchEvent(customEvent);
};

/**
 * Dispatch booking post-commit update events to BookingConfirmation page.
 * Fired when the server completes video/calendar creation after the initial booking response.
 */
const dispatchBookingPostCommitUpdate = (event: any): void => {
  const customEvent = new CustomEvent('booking-post-commit-update', {
    detail: {
      bookingId: event.bookingId,
      videoIntegrationStatus: event.videoIntegrationStatus,
      videoIntegrationError: event.videoIntegrationError,
      meetingJoinUrl: event.meetingJoinUrl,
      timestamp: event.timestamp
    }
  });
  window.dispatchEvent(customEvent);
};

/**
 * Dispatch organization activity change events to admin panel components
 */
const dispatchOrganizationActivityChange = (event: any): void => {
  // Create custom event for OrganizationActivityPanel to listen to
  const customEvent = new CustomEvent('organization-activity-changed', {
    detail: {
      organizationId: event.organizationId,
      activityLog: event.activityLog,
      timestamp: event.timestamp
    }
  });

  window.dispatchEvent(customEvent);
};

// Default export for convenient importing
const cacheInvalidationService = {
  initCacheInvalidationService,
  invalidateMeetingTypeSlots,
  invalidateAllMeetingTypeCache,
  clearAllCaches
};

export default cacheInvalidationService;